import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studySessionAPI, questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { HighlightableText } from '../components/HighlightableText';
import { ExplanationRenderer } from '../components/core/ExplanationRenderer';
import { SUBJECT_COLORS, DIFFICULTY_COLORS, cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    ArrowRight,
    Clock,
    Lightbulb,
    Trophy,
    Target,
    BookOpen,
    ArrowLeft
} from 'lucide-react';
import Layout from '../components/Layout';

export default function StudySession() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    
    const [session, setSession] = useState(null);
    const [question, setQuestion] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingQuestion, setLoadingQuestion] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lockedAnswer, setLockedAnswer] = useState(null);
    const [result, setResult] = useState(null);
    const [strikethrough, setStrikethrough] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [sessionComplete, setSessionComplete] = useState(false);
    const [sessionResults, setSessionResults] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);

    // Poll for background-generated image
    useEffect(() => {
        if (!question) return;
        if (question.image_base64) return;
        if (!question.image_description) return;
        if (question.image_ready) return;

        setLoadingImage(true);
        let cancelled = false;
        const poll = async () => {
            for (let i = 0; i < 20; i++) {
                if (cancelled) return;
                await new Promise(r => setTimeout(r, 2000));
                if (cancelled) return;
                try {
                    const res = await questionsAPI.getImage(question.id);
                    if (res.data.status === 'ready' && res.data.image_base64) {
                        if (!cancelled) {
                            setQuestion(prev => prev?.id === question.id ? { ...prev, image_base64: res.data.image_base64, image_ready: true } : prev);
                            setLoadingImage(false);
                        }
                        return;
                    }
                    if (res.data.status === 'no_image') {
                        setLoadingImage(false);
                        return;
                    }
                } catch { /* continue polling */ }
            }
            if (!cancelled) setLoadingImage(false);
        };
        poll();
        return () => { cancelled = true; };
    }, [question?.id, question?.image_base64, question?.image_description, question?.image_ready]);

    // Timer effect
    useEffect(() => {
        let interval;
        if (startTime && !submitted) {
            interval = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [startTime, submitted]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Fetch session details
    useEffect(() => {
        const fetchSession = async () => {
            try {
                const response = await studySessionAPI.getSession(sessionId);
                setSession(response.data);
                if (response.data.status === 'completed') {
                    setSessionComplete(true);
                    setSessionResults(response.data.results);
                }
            } catch (err) {
                console.error('Failed to fetch session:', err);
                toast.error('Failed to load study session');
                navigate('/study-planner');
            } finally {
                setLoading(false);
            }
        };
        fetchSession();
    }, [sessionId, navigate]);

    // Load initial question
    useEffect(() => {
        if (session && session.status === 'in_progress' && !question) {
            fetchNextQuestion();
        }
    }, [session]);

    const fetchNextQuestion = async () => {
        setLoadingQuestion(true);
        setSelectedAnswer(null);
        setSubmitted(false);
        setSubmitting(false);
        setLockedAnswer(null);
        setResult(null);
        setStrikethrough([]);
        setStartTime(null);
        setElapsed(0);

        try {
            const response = await studySessionAPI.getQuestion(sessionId);
            
            if (response.data.status === 'completed') {
                setSessionComplete(true);
                // Fetch final results
                const sessionResponse = await studySessionAPI.getSession(sessionId);
                setSessionResults(sessionResponse.data.results);
                toast.success('Study session complete!');
            } else {
                setQuestion(response.data.question);
                setProgress(response.data.progress);
                setStartTime(Date.now());
            }
        } catch (err) {
            console.error('Failed to fetch question:', err);
            toast.error('Failed to load question');
        } finally {
            setLoadingQuestion(false);
        }
    };

    const submitAnswer = async () => {
        if (!selectedAnswer || !question || submitting) return;

        // Lock answer immediately — no more changes allowed
        const answeredWith = selectedAnswer;
        setLockedAnswer(answeredWith);
        setSubmitting(true);

        try {
            const response = await questionsAPI.submitAnswer(question.id, {
                question_id: question.id,
                selected_answer: answeredWith,
                time_spent_seconds: elapsed,
                session_id: sessionId
            });
            
            setResult(response.data);
            setSubmitted(true);
            
            if (response.data.is_correct) {
                toast.success('Correct!');
            } else {
                toast.error('Incorrect');
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Failed to submit answer: ${detail}. Click "Submit" to try again.`);
            // Unlock on error so user can retry — keep selectedAnswer so they don't have to re-select
            setSubmitting(false);
            setLockedAnswer(null);
        }
    };

    const toggleStrikethrough = (letter) => {
        if (submitted) return;
        setStrikethrough(prev => 
            prev.includes(letter) 
                ? prev.filter(l => l !== letter)
                : [...prev, letter]
        );
    };

    const getOptionStyle = (option) => {
        const answered = lockedAnswer || selectedAnswer;
        if (!submitted && !submitting) {
            if (selectedAnswer === option.letter) {
                return isDark ? 'border-indigo-500 bg-indigo-900/40' : 'border-indigo-500 bg-indigo-50';
            }
            return isDark ? 'border-slate-600 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300';
        }

        // Submitting (locked but waiting for result)
        if (submitting && !submitted) {
            if (answered === option.letter) {
                return isDark ? 'border-indigo-500 bg-indigo-900/40' : 'border-indigo-500 bg-indigo-50';
            }
            return isDark ? 'border-slate-700 opacity-60' : 'border-slate-200 opacity-60';
        }

        // Submitted with result
        if (option.letter === result?.correct_answer) {
            return isDark ? 'border-emerald-500 bg-emerald-900/50' : 'border-emerald-500 bg-emerald-50';
        }
        if (answered === option.letter && !result?.is_correct) {
            return isDark ? 'border-red-500 bg-red-900/50' : 'border-red-500 bg-red-50';
        }
        return isDark ? 'border-slate-700 opacity-60' : 'border-slate-200 opacity-60';
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </Layout>
        );
    }

    if (sessionComplete) {
        return (
            <Layout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="session-complete">
                    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                        <CardContent className="p-8 text-center">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                                <Trophy className="h-10 w-10 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                                Session Complete!
                            </h1>
                            <p className="text-slate-600 mb-6">
                                Great job on completing your study session for <strong>{session?.plan_name}</strong>
                            </p>

                            {sessionResults && (
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="p-4 rounded-lg bg-white border border-slate-200">
                                        <p className="text-3xl font-bold text-emerald-600">{sessionResults.correct}</p>
                                        <p className="text-sm text-slate-500">Correct</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-white border border-slate-200">
                                        <p className="text-3xl font-bold text-red-500">{sessionResults.incorrect}</p>
                                        <p className="text-sm text-slate-500">Incorrect</p>
                                    </div>
                                    <div className="col-span-2 p-4 rounded-lg bg-white border border-slate-200">
                                        <p className="text-3xl font-bold text-indigo-600">{sessionResults.accuracy}%</p>
                                        <p className="text-sm text-slate-500">Accuracy</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 justify-center">
                                <Button variant="outline" onClick={() => navigate('/study-planner')}>
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Planner
                                </Button>
                                <Button 
                                    onClick={() => navigate('/analytics')}
                                    className="bg-gradient-to-r from-indigo-500 to-purple-500"
                                >
                                    <Target className="mr-2 h-4 w-4" />
                                    View Analytics
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="study-session-container">
                {/* Header with Progress */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BookOpen className="h-5 w-5 text-indigo-500" />
                            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                {session?.plan_name}
                            </h1>
                        </div>
                        <p className="text-sm text-slate-500">
                            Question {progress.current} of {progress.total}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="h-4 w-4" />
                            <span className="font-mono">{formatTime(elapsed)}</span>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate('/study-planner')}
                        >
                            Exit Session
                        </Button>
                    </div>
                </div>

                {/* Progress Bar */}
                <Progress 
                    value={(progress.current / progress.total) * 100} 
                    className="h-2"
                />

                {/* Question Card */}
                {loadingQuestion ? (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-indigo-500" />
                            <p className="text-slate-600">Generating your next question...</p>
                        </CardContent>
                    </Card>
                ) : question ? (
                    <Card>
                        <CardHeader className="pb-4">
                            <div className="flex flex-wrap gap-2 mb-4">
                                <Badge className={cn(
                                    SUBJECT_COLORS[question.subject]?.bg || 'bg-slate-100',
                                    SUBJECT_COLORS[question.subject]?.text || 'text-slate-700'
                                )}>
                                    {question.subject}
                                </Badge>
                                {question.organ_system && (
                                    <Badge variant="outline">
                                        {question.organ_system}
                                    </Badge>
                                )}
                            </div>

                            {/* Vignette */}
                            <div className="prose prose-sm max-w-none">
                                <HighlightableText 
                                    text={question.vignette} 
                                    className="text-slate-700 leading-relaxed"
                                />
                            </div>

                            {/* Image if present */}
                            {question.image_base64 && (
                                <div className="mt-4 rounded-lg overflow-hidden border border-slate-200 animate-in fade-in duration-500">
                                    <ImageZoom 
                                        src={`data:image/png;base64,${question.image_base64}`}
                                        alt="Medical illustration"
                                    />
                                    {submitted && question.image_description && (
                                        <p className="text-xs text-slate-500 mt-2 italic px-2 pb-2">
                                            {question.image_description}
                                        </p>
                                    )}
                                </div>
                            )}
                            {/* Image loading shimmer */}
                            {!question.image_base64 && loadingImage && (
                                <div className="mt-4 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 h-48 flex items-center justify-center">
                                    <div className="flex flex-col items-center gap-2 text-slate-400">
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                        <span className="text-sm">Loading medical image...</span>
                                    </div>
                                </div>
                            )}

                            {/* Question Stem */}
                            <CardTitle className="text-lg mt-4" style={{ fontFamily: 'Manrope' }}>
                                {question.question_stem}
                            </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-3">
                            {/* Options */}
                            {question.options?.map((option) => {
                                const answered = lockedAnswer || selectedAnswer;
                                const isLocked = submitted || submitting;
                                return (
                                <button
                                    key={option.letter}
                                    onClick={() => !isLocked && setSelectedAnswer(option.letter)}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        toggleStrikethrough(option.letter);
                                    }}
                                    disabled={isLocked}
                                    className={cn(
                                        "w-full text-left p-4 rounded-lg border-2 transition-all",
                                        getOptionStyle(option),
                                        strikethrough.includes(option.letter) && !isLocked && "opacity-40 line-through"
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={cn(
                                            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                            answered === option.letter && !submitted
                                                ? "bg-indigo-500 text-white"
                                                : submitted && option.letter === result?.correct_answer
                                                    ? "bg-emerald-500 text-white"
                                                    : submitted && answered === option.letter && !result?.is_correct
                                                        ? "bg-red-500 text-white"
                                                        : isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                        )}>
                                            {option.letter}
                                        </span>
                                        <span className={cn(
                                            "flex-1 pt-1",
                                            !submitted && isDark && "text-slate-200",
                                            submitted && option.letter === result?.correct_answer && !isDark && "text-emerald-900",
                                            submitted && option.letter === result?.correct_answer && isDark && "text-emerald-200",
                                            submitted && answered === option.letter && !result?.is_correct && !isDark && "text-red-900",
                                            submitted && answered === option.letter && !result?.is_correct && isDark && "text-red-200"
                                        )}>{option.text}</span>
                                        {submitted && option.letter === result?.correct_answer && (
                                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                                        )}
                                        {submitted && answered === option.letter && !result?.is_correct && (
                                            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                        )}
                                    </div>
                                </button>
                                );
                            })}

                            {/* Explanation */}
                            {submitted && result && (
                                <div className={cn(
                                    "mt-6 p-4 rounded-lg",
                                    result.is_correct && !isDark && "bg-emerald-100 border border-emerald-500",
                                    result.is_correct && isDark && "bg-emerald-900/50 border border-emerald-500",
                                    !result.is_correct && !isDark && "bg-amber-100 border border-amber-500",
                                    !result.is_correct && isDark && "bg-amber-900/50 border border-amber-500"
                                )}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Lightbulb className={cn(
                                            "h-5 w-5",
                                            result.is_correct && "text-emerald-500",
                                            !result.is_correct && "text-amber-500"
                                        )} />
                                        <span className={cn(
                                            "font-semibold",
                                            result.is_correct && !isDark && "text-emerald-800",
                                            result.is_correct && isDark && "text-emerald-300",
                                            !result.is_correct && !isDark && "text-amber-800",
                                            !result.is_correct && isDark && "text-amber-300"
                                        )}>Explanation</span>
                                    </div>
                                    <ExplanationRenderer
                                        text={result.explanation || question.explanation || "The correct answer demonstrates the key concept being tested."}
                                        className={cn(
                                            "text-sm",
                                            result.is_correct && !isDark && "text-emerald-900",
                                            result.is_correct && isDark && "text-emerald-200",
                                            !result.is_correct && !isDark && "text-amber-900",
                                            !result.is_correct && isDark && "text-amber-200"
                                        )}
                                    />
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4">
                                {!submitted ? (
                                    <Button
                                        onClick={submitAnswer}
                                        disabled={!selectedAnswer || submitting}
                                        className="bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="submit-answer-btn"
                                    >
                                        {submitting ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>
                                        ) : (
                                            'Submit Answer'
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={fetchNextQuestion}
                                        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                                    >
                                        {progress.current >= progress.total ? 'Finish Session' : 'Next Question'}
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : null}

                {/* Tips */}
                <p className="text-xs text-slate-400 text-center">
                    Tip: Right-click on an option to strike it through
                </p>
            </div>
        </Layout>
    );
}
